'use client';

/**
 * The account email, editable, with the sign-in-again step the server asks
 * for (S-229 update and S-232, 2026-09-24).
 *
 * `PATCH /api/users/me` refuses an email change unless the browser session
 * was minted in the last few minutes (lib/auth/recent-auth.ts), answering
 * 403 `{ error: 'reauth_required' }`. This component turns that answer into
 * an inline sign-in rather than a dead end:
 *
 *   - with a passkey on the account, the WebAuthn assertion runs in place.
 *     The verify route mints a fresh cookie, and the change is retried at
 *     once; the person never leaves the field.
 *   - otherwise the providers linked to THIS account are offered, each
 *     returning to this tab. Only linked ones: an unlinked provider would
 *     sign the person into a different (or a brand-new) account.
 *   - with neither, the only sign-in left is the one that created the
 *     account, so the panel says so and offers sign-out.
 *
 * The address waiting on a provider round trip is parked in sessionStorage,
 * keyed by user id, never in the URL: a `returnTo` is logged, shared and
 * replayed, and an email is personal data. The assertion may also sign the
 * browser in as another account (a passkey belongs to whoever registered
 * it), so the session is re-read after it and the retry only runs for the
 * same user.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Fingerprint, LogOut } from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import { ProviderIcon } from '@/components/provider-icon';
import { Spinner } from '@/components/ui/spinner';
import { isGuestUser } from '@/lib/auth/is-guest';

/** Where a provider sign-in comes back to: the settings dialog, profile tab. */
export const EMAIL_CHANGE_RETURN_TO = '/settings?tab=profile';

/** sessionStorage key for the address waiting on a sign-in. */
export const PENDING_EMAIL_KEY = 'robutler_pending_email';

/** A parked address older than this is dropped rather than prefilled. */
const PENDING_EMAIL_TTL_MS = 30 * 60_000;

/** The providers that sign a person in, with their login-screen labels. */
const SIGN_IN_PROVIDERS = [
  { id: 'google', labelKey: 'auth.continueWithGoogle' },
  { id: 'x', labelKey: 'auth.continueWithX' },
  { id: 'discord', labelKey: 'auth.continueWithDiscord' },
] as const;

type SignInProviderId = (typeof SIGN_IN_PROVIDERS)[number]['id'];

type Notice = 'saved' | 'returned' | null;
type Failure = 'invalid' | 'inUse' | 'failed' | 'passkey' | 'otherAccount' | null;

interface PasskeyRow {
  id: string;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Park `email` for `userId` across a provider round trip. */
export function stashPendingEmail(userId: string, email: string, store: Storage | null = storage()): void {
  try {
    store?.setItem(PENDING_EMAIL_KEY, JSON.stringify({ userId, email, at: Date.now() }));
  } catch {
    /* no storage: the person types the address again */
  }
}

export function clearPendingEmail(store: Storage | null = storage()): void {
  try {
    store?.removeItem(PENDING_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The address parked for `userId`, or null. One-shot: the entry is removed
 * on read, whoever it was for, so a stale one never prefills a later visit
 * or another account in the same browser.
 */
export function takePendingEmail(userId: string, store: Storage | null = storage()): string | null {
  try {
    const raw = store?.getItem(PENDING_EMAIL_KEY);
    if (!raw) return null;
    store?.removeItem(PENDING_EMAIL_KEY);
    const parsed = JSON.parse(raw) as { userId?: unknown; email?: unknown; at?: unknown };
    if (parsed.userId !== userId) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > PENDING_EMAIL_TTL_MS) return null;
    return typeof parsed.email === 'string' ? parsed.email : null;
  } catch {
    return null;
  }
}

/** The sign-in URL for a linked provider, returning to this tab. */
export function providerReauthUrl(provider: SignInProviderId): string {
  return `/api/auth/${provider}?returnTo=${encodeURIComponent(EMAIL_CHANGE_RETURN_TO)}`;
}

export function EmailChange() {
  const { t } = useTranslation();
  const { user, connectedAccounts, refresh, logout } = useAuth();
  const [draft, setDraft] = useState(user?.email ?? '');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [reauth, setReauth] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([]);
  const consumedStashFor = useRef<string | null>(null);

  // Follow the account's address until the person starts typing; after a
  // successful save the field is untouched again, so the refreshed session
  // value flows back in.
  useEffect(() => {
    if (!touched) setDraft(user?.email ?? '');
  }, [user?.email, touched]);

  // Back from a provider: prefill the parked address for THIS account.
  useEffect(() => {
    const id = user?.id;
    if (!id || consumedStashFor.current === id) return;
    consumedStashFor.current = id;
    const pending = takePendingEmail(id);
    if (pending === null) return;
    setDraft(pending);
    setTouched(true);
    setNotice('returned');
  }, [user?.id]);

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/webauthn/passkeys');
      if (!res.ok) return;
      const data = (await res.json()) as { passkeys?: PasskeyRow[] };
      setPasskeys(Array.isArray(data.passkeys) ? data.passkeys : []);
    } catch {
      setPasskeys([]);
    }
  }, []);

  const save = useCallback(async () => {
    if (!user) return;
    const value = draft.trim();
    setSaving(true);
    setFailure(null);
    setNotice(null);
    try {
      const res = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value === '' ? null : value }),
      });
      if (res.ok) {
        clearPendingEmail();
        setReauth(false);
        setTouched(false);
        setNotice('saved');
        await refresh();
        return;
      }
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      if (res.status === 403 && body?.error === 'reauth_required') {
        stashPendingEmail(user.id, draft);
        setReauth(true);
        void loadPasskeys();
        return;
      }
      if (res.status === 400 && body?.error === 'invalid_email') setFailure('invalid');
      else if (res.status === 409 && body?.error === 'email_in_use') setFailure('inUse');
      else setFailure('failed');
    } catch {
      setFailure('failed');
    } finally {
      setSaving(false);
    }
  }, [draft, user, refresh, loadPasskeys]);

  const confirmWithPasskey = useCallback(async () => {
    if (!user) return;
    setConfirming(true);
    setFailure(null);
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const optionsRes = await fetch('/api/auth/webauthn/authenticate');
      if (!optionsRes.ok) {
        setFailure('passkey');
        return;
      }
      const options = await optionsRes.json();
      // Offer only this account's passkeys, so the browser's picker cannot
      // steer the person into another account they also hold. The server
      // verifies whichever credential answers; the identity check below is
      // what actually guards the retry.
      options.allowCredentials = passkeys.map((p) => ({ id: p.id, type: 'public-key' }));
      const assertion = await startAuthentication({ optionsJSON: options });
      const verifyRes = await fetch('/api/auth/webauthn/authenticate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assertion),
      });
      const result = verifyRes.ok ? ((await verifyRes.json()) as { verified?: boolean }) : null;
      if (!result?.verified) {
        setFailure('passkey');
        return;
      }
      const who = (await fetch('/api/auth/session')
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)) as { user?: { id?: string } } | null;
      if (who?.user?.id !== user.id) {
        // The browser is now signed in as the passkey's owner, a different
        // account. Nothing is retried; the page reloads into that account.
        setFailure('otherAccount');
        window.setTimeout(() => window.location.reload(), 1500);
        return;
      }
      setReauth(false);
      await save();
    } catch (err) {
      // A dismissed prompt is not an error.
      if ((err as { name?: string } | null)?.name !== 'NotAllowedError') setFailure('passkey');
    } finally {
      setConfirming(false);
    }
  }, [user, passkeys, save]);

  if (!user) return null;

  if (isGuestUser(user)) {
    // A guest has no sign-in to repeat; the address arrives with the upgrade.
    return (
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.email')}</label>
        <input
          type="email"
          value={user.email || ''}
          readOnly
          disabled
          className="input w-full opacity-70 cursor-not-allowed"
          placeholder={t('settings.noEmailOnFile')}
        />
      </div>
    );
  }

  const current = (user.email ?? '').trim().toLowerCase();
  const dirty = draft.trim().toLowerCase() !== current;
  const linkedProviders = SIGN_IN_PROVIDERS.filter((p) =>
    connectedAccounts.some((account) => account.provider === p.id),
  );
  const busy = saving || confirming;

  const failureText =
    failure === 'invalid'
      ? t('settings.emailInvalid')
      : failure === 'inUse'
        ? t('settings.emailInUse')
        : failure === 'passkey'
          ? t('settings.emailReauthPasskeyFailed')
          : failure === 'otherAccount'
            ? t('settings.emailReauthOtherAccount')
            : failure === 'failed'
              ? t('settings.emailChangeFailed')
              : null;

  return (
    <div>
      <label htmlFor="settings-email" className="block text-sm font-medium mb-2">
        {t('settings.email')}
      </label>
      <div className="flex gap-2">
        <input
          id="settings-email"
          type="email"
          value={draft}
          onChange={(e) => {
            setTouched(true);
            setDraft(e.target.value);
            setNotice(null);
            setFailure(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && dirty && !busy) {
              e.preventDefault();
              void save();
            }
          }}
          disabled={busy}
          autoComplete="email"
          className="input w-full"
          placeholder={t('settings.emailPlaceholder')}
        />
        {dirty && (
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="btn-outline shrink-0 flex items-center gap-2"
            data-testid="email-save"
          >
            {saving ? <Spinner size="sm" /> : null}
            {t('settings.saveEmail')}
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-1">{t('settings.emailHelp')}</p>

      {notice === 'saved' && (
        <p className="mt-2 text-xs text-green-500 flex items-center gap-1" role="status">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {t('settings.emailSaved')}
        </p>
      )}
      {notice === 'returned' && (
        <p className="mt-2 text-xs text-muted-foreground" role="status">
          {t('settings.emailReauthReturned')}
        </p>
      )}
      {failureText && (
        <p className="mt-2 text-xs text-destructive flex items-center gap-1" role="alert">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {failureText}
        </p>
      )}

      {reauth && (
        <div
          className="mt-3 space-y-3 rounded-lg border border-border/60 bg-muted/30 p-3"
          data-testid="email-reauth"
        >
          <p className="text-sm">{t('settings.emailReauthRequired')}</p>

          {passkeys.length > 0 && (
            <button
              type="button"
              onClick={() => void confirmWithPasskey()}
              disabled={busy}
              className="btn-primary w-full justify-center flex items-center gap-2"
              data-testid="email-reauth-passkey"
            >
              {confirming ? <Spinner size="sm" /> : <Fingerprint className="h-4 w-4" aria-hidden="true" />}
              {t('settings.emailReauthPasskey')}
            </button>
          )}

          {linkedProviders.length > 0 && (
            <div className="space-y-2">
              {passkeys.length > 0 && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  {t('settings.emailReauthOr')}
                  <span className="h-px flex-1 bg-border" />
                </div>
              )}
              {linkedProviders.map((p) => (
                <a
                  key={p.id}
                  href={providerReauthUrl(p.id)}
                  onClick={() => stashPendingEmail(user.id, draft)}
                  className="btn-outline w-full justify-center flex items-center gap-2"
                  data-testid={`email-reauth-${p.id}`}
                >
                  <ProviderIcon slug={p.id} size={16} />
                  {t(p.labelKey)}
                </a>
              ))}
            </div>
          )}

          {passkeys.length === 0 && linkedProviders.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('settings.emailReauthNoMethod')}</p>
              <button
                type="button"
                onClick={() => void logout()}
                className="btn-outline w-full justify-center flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t('auth.signOut')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
